import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import DropZoneCSV from "components/shared/UploadCsv";
import { SmallBody } from "helpers/TypographyComponents";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { uploadInterforumEdiFile } from "store/automatedImportStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { EFetchStatus } from "store/types";

import {
    INTERFORUM_EDI_MAX_UPLOAD_BYTES,
    INTERFORUM_EDI_MAX_UPLOAD_MB,
    INTERFORUM_COMPANY_BY_ID,
    interforumEdiFileNameValidator,
} from "./interforumEdiUploadUtils";
import styles from "./style.module.scss";

interface InterforumEdiUploadModalProps {
    open: boolean;
    onClose: () => void;
    companyId?: number;
}

const InterforumEdiUploadModal = ({ open, onClose, companyId }: InterforumEdiUploadModalProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const uploadEdiFileStatus = useAppSelector((state) => state.automatedImports.uploadEdiFileStatus);
    const [files, setFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const isProcessing = uploadEdiFileStatus === EFetchStatus.PENDING;
    const interforumCompany = companyId ? INTERFORUM_COMPANY_BY_ID[companyId] : undefined;
    const _isLoading = isLoading || isProcessing;

    useEffect(() => {
        if (!open) {
            setFiles([]);
        }
    }, [open]);

    const handleSubmit = async () => {
        const file = files[0];
        if (!file || _isLoading) {
            return;
        }

        try {
            setIsLoading(true);
            await dispatch(uploadInterforumEdiFile(file)).unwrap();
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            mainTitle={t("Settings.EDIInterforum.UploadModalTitle")}
            subTitle={t("Settings.EDIInterforum.UploadHelp")}
            showCloseButton
        >
            <div className={styles["upload-modal-body"]}>
                <DropZoneCSV
                    setFile={setFiles}
                    file={files}
                    acceptAnyFile
                    maxSize={INTERFORUM_EDI_MAX_UPLOAD_BYTES}
                    customValidator={interforumEdiFileNameValidator}
                    rejectedHint={t("Settings.EDIInterforum.UploadInvalidFileName")}
                    hintText={t("Settings.EDIInterforum.UploadDropzoneHint", { size: INTERFORUM_EDI_MAX_UPLOAD_MB })}
                />
                {interforumCompany && (
                    <SmallBody color="grey">
                        {t("Settings.EDIInterforum.UploadFileNameHint", { company: interforumCompany })}
                    </SmallBody>
                )}
                <div className={styles["upload-modal-actions"]}>
                    <StyledButton buttonType="outlined" onClick={onClose} fullWidth>
                        {t("ModalBase.Cancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        onClick={handleSubmit}
                        disabled={files.length === 0 || _isLoading}
                        loading={_isLoading}
                        fullWidth
                    >
                        {_isLoading
                            ? t("Settings.EDIInterforum.UploadInProgress")
                            : t("Settings.EDIInterforum.UploadSubmit")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default InterforumEdiUploadModal;
